/** @jsx jsx */
import { Component, Fragment } from 'react';
import { WrappedComponentProps, injectIntl } from 'react-intl';

import { jsx } from '@emotion/core';
import { Spacer } from '@zapier/design-system';

import SaveButtonGroup from 'app/developer-v3/components/SaveButtonGroup';
import SimpleMdeWrapper from 'app/common/components/SimpleMdeWrapper';

import type { FormVariables } from './Stream/types';
import type { Comment as IssueComment } from 'app/developer-v3/hooks/useIssue';

type SmdeEvent = {
  doc: {
    getValue: () => string;
  };
};

type Props = WrappedComponentProps & {
  comment?: IssueComment;
  isSaving: boolean;
  onCancelEdit?: () => void;
  onSubmit: (variables: FormVariables) => Promise<void>;
  value?: string;
};

type State = {
  commentText: string;
};

class CommentEditor extends Component<Props, State> {
  static defaultProps = {
    value: '',
  };

  state = {
    commentText: this.props.value,
  };

  get commentBody() {
    return this.state.commentText;
  }

  get commentId() {
    const { comment } = this.props;
    // getting `undefined` here indicates that this is a new comment
    return comment?.id;
  }

  onSaveComment = async () => {
    const { onSubmit, value } = this.props;
    const isNewComment = !value;

    try {
      await onSubmit({
        body: this.commentBody,
        commentId: this.commentId,
      });
    } catch (error) {
      // Swallow for now. This is only from 500 server errors from Loki
    }

    if (isNewComment) {
      this.setState({ commentText: '' });
    }
  };

  onChange = (event: SmdeEvent) => {
    this.setState({
      commentText: event.doc.getValue(),
    });
  };

  render() {
    const { commentText } = this.state;
    const { intl, isSaving, onCancelEdit } = this.props;

    return (
      <Fragment>
        <SimpleMdeWrapper onChange={this.onChange} value={commentText} />
        <Spacer height="20px" />
        <SaveButtonGroup
          isDisabled={commentText.trim().length === 0}
          isSaving={isSaving}
          onCancel={onCancelEdit}
          onSave={this.onSaveComment}
          saveColor={onCancelEdit ? 'primary' : 'secondary'}
          saveText={intl.formatMessage({
            defaultMessage: 'Save comment',
            id: 'issues.comment_editor.save',
          })}
        />
      </Fragment>
    );
  }
}

export default injectIntl(CommentEditor);
